import { motion } from 'framer-motion'
import type { Donation } from './types'
import { PAYMENT_METHODS, CURRENCIES } from './types'
import { Paper, Typography, Box, Divider, Avatar } from '@mui/material'

interface RecentDonationsProps {
  donations: Donation[]
}

const formatDate = (date: Date): string => {
  const months = [
    'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
    'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
  ]
  const day = date.getDate()
  const month = months[date.getMonth()]
  const year = date.getFullYear()
  return `${day} ${month} ${year}`
}


export default function RecentDonations({ donations }: RecentDonationsProps) {
  if (donations.length === 0) {
    return null
  }

  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.4 }}>
      <Paper elevation={3} sx={{ mt: 4, borderRadius: 3, p: { xs: 2, sm: 3 } }}>
        <Box sx={{ textAlign: 'center', mb: 3 }}>
          <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>
            Recent Donations
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            Thank you to our generous supporters.
          </Typography>
        </Box>

        <Box>
          {donations.map((donation, index) => {
            const isLast = index === donations.length - 1
            const paymentMethod = PAYMENT_METHODS.find((pm) => pm.id === donation.paymentMethod)
            const currency = CURRENCIES.find((c) => c.code === donation.currency)
            const currencySymbol = currency?.symbol || donation.currency

            return (
              <Box key={donation.id} sx={{ py: 2 }}>
                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: { sm: 'center' }, justifyContent: { sm: 'space-between' }, gap: 1.5 }}>
                  <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: { sm: 'center' }, gap: 1.5 }}>
                    <Typography sx={{ fontWeight: 700, color: 'text.primary' }}>
                      {currencySymbol}
                      {donation.amount.toFixed(2)} {donation.currency}
                    </Typography>
                    <Typography sx={{ color: 'text.secondary' }}>
                      {donation.email ? `from ${donation.email}` : '(Anonymous)'}
                    </Typography>
                    {paymentMethod && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {paymentMethod.image ? (
                          <Avatar
                            src={paymentMethod.image}
                            alt={paymentMethod.name}
                            variant="rounded"
                            sx={{ width: 24, height: 24 }}
                          />
                        ) : (
                          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                            {paymentMethod.name}
                          </Typography>
                        )}
                      </Box>
                    )}
                  </Box>
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                    <Typography sx={{ color: '#b45309', fontWeight: 500 }}>{donation.status}</Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                      {formatDate(donation.date)}
                    </Typography>
                  </Box>
                </Box>
                {!isLast && <Divider sx={{ mt: 2 }} />}
              </Box>
            )
          })}
        </Box>
      </Paper>
    </motion.div>
  )
}

